import React, { useEffect, useMemo, useState } from "react";
import { SUBSYS } from "../protocol/frameProtocol";
import { EMULATOR_CATALOG, hexByte } from "../data/emulatorCatalog";
import {
  connectWebSerial,
  getWebSerialStatus,
  onWebSerialData,
  onWebSerialStatus,
  sendProtocolCommandViaWebSerial,
} from "../services/webSerial";
import FrameInspector from "./FrameInspector";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:3001";
const SUBSYSTEMS = [
  { name: "EPS", address: SUBSYS.EPS },
  { name: "TLM", address: SUBSYS.TLM },
  { name: "COMMS", address: SUBSYS.COMMS },
  { name: "PAYLOAD", address: SUBSYS.PAYLOAD },
];
const card = { padding: 18, border: "1px solid rgba(255,255,255,.11)", borderRadius: 18, background: "rgba(255,255,255,.045)" };
const button = { padding: "10px 14px", border: "1px solid rgba(0,223,255,.38)", borderRadius: 11, color: "#00dfff", background: "rgba(0,223,255,.11)", fontWeight: 800, cursor: "pointer" };
const input = { minHeight: 42, padding: "9px 11px", border: "1px solid rgba(255,255,255,.16)", borderRadius: 10, color: "#fff", background: "#171717" };

export default function SubsystemCommands() {
  const [webStatus, setWebStatus] = useState(getWebSerialStatus());
  const [subsystem, setSubsystem] = useState("EPS");
  const [variableId, setVariableId] = useState(0);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("Conecta la tarjeta para ejecutar los comandos implementados.");
  const [lastFrame, setLastFrame] = useState(null);
  const selectedSubsystem = useMemo(() => SUBSYSTEMS.find((item) => item.name === subsystem), [subsystem]);
  const variables = EMULATOR_CATALOG[subsystem] || [];

  useEffect(() => {
    const offStatus = onWebSerialStatus(setWebStatus);
    const offData = onWebSerialData((event) => event.direction === "frame" && setLastFrame(event));
    return () => { offStatus(); offData(); };
  }, []);

  useEffect(() => setVariableId(variables[0]?.id ?? 0), [subsystem]); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async () => {
    try {
      setBusy("connect");
      await connectWebSerial({ baudRate: 115200 });
      setMessage("Tarjeta conectada. Ya puedes enviar comandos binarios.");
    } catch (error) { setMessage(error.message); }
    finally { setBusy(""); }
  };

  const runCommand = async (command, options = {}, expectedCommand = "ACK") => {
    try {
      setBusy(command);
      setMessage(`Enviando ${command} a ${subsystem}...`);
      const result = await sendProtocolCommandViaWebSerial(API_BASE, { subsystem, command, ...options }, { expectedCommand, timeoutMs: 3500 });
      if (result.generatedOnly) {
        setMessage(`${command} generado, no enviado porque falta conectar la tarjeta. HEX ${result.frameHex}`);
      } else if (command === "READ_VARIABLE") {
        setMessage(`Variable ${hexByte(result.response?.details?.variableId ?? variableId)} recibida: ${result.response?.details?.value ?? "valor no decodificado"}.`);
      } else if (command === "STATUS") {
        const size = result.response?.payload?.length ?? 0;
        setMessage(`ESTADO recibido con ${size} byte(s)${size === 8 ? " (longitud esperada)" : " (el PDF indica 8)"}.`);
      } else {
        setMessage(`${command} confirmado por ${result.response?.commandName || "la tarjeta"}.`);
      }
      if (result.response) setLastFrame(result.response);
    } catch (error) { setMessage(`${command}: ${error.message}`); }
    finally { setBusy(""); }
  };

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <section style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <strong style={{ color: "#00dfff", fontSize: 19 }}>Comandos de operación de los esclavos</strong>
            <p style={{ color: "#b8c2cc", margin: "7px 0 0", lineHeight: 1.5 }}>PING, LEER_VAR, DORMIR, ESTADO, REINICIAR y pruebas F0/F1/F2. El modo global y el historial del maestro quedan pendientes del firmware.</p>
          </div>
          <button type="button" onClick={connect} disabled={busy === "connect" || webStatus.connected} style={button}>{webStatus.connected ? "Tarjeta conectada" : "Conectar tarjeta"}</button>
        </div>
      </section>

      <section style={{ ...card, display: "grid", gap: 14 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "end", flexWrap: "wrap" }}>
          <label style={{ display: "grid", gap: 6, color: "#b8c2cc" }}>Esclavo
            <select value={subsystem} onChange={(event) => setSubsystem(event.target.value)} style={input}>
              {SUBSYSTEMS.map((item) => <option key={item.name} value={item.name}>{item.name} · SUB {hexByte(item.address)}</option>)}
            </select>
          </label>
          <button type="button" onClick={() => runCommand("PING", {}, "PONG")} disabled={Boolean(busy)} style={button}>PING · 0x13</button>
          <button type="button" onClick={() => runCommand("STATUS", {}, "STATUS")} disabled={Boolean(busy)} style={button}>ESTADO · 0x31</button>
          <button type="button" onClick={() => runCommand("SLEEP")} disabled={Boolean(busy)} style={button}>DORMIR · 0x30</button>
          <button type="button" onClick={() => runCommand("RESET")} disabled={Boolean(busy)} style={button}>REINICIAR · 0x32</button>
        </div>
      </section>

      <section style={card}>
        <strong style={{ color: "#00dfff" }}>Lectura de variable · 0x20</strong>
        <div style={{ display: "flex", gap: 10, alignItems: "end", flexWrap: "wrap", marginTop: 12 }}>
          {variables.length ? <label style={{ display: "grid", gap: 6, color: "#b8c2cc" }}>Variable del subsistema
            <select value={variableId} onChange={(event) => setVariableId(Number(event.target.value))} style={input}>
              {variables.map((variable) => <option key={variable.key} value={variable.id}>{hexByte(variable.id)} · {variable.name}</option>)}
            </select>
          </label> : <span style={{ color: "#94a3b8" }}>PAYLOAD no usa LEER_VAR en la tabla recibida.</span>}
          <button type="button" onClick={() => runCommand("READ_VARIABLE", { variableId }, "READ_VARIABLE")} disabled={Boolean(busy) || !variables.length} style={button}>LEER_VAR</button>
        </div>
      </section>

      <section style={card}>
        <strong style={{ color: "#00dfff" }}>Pruebas de diagnóstico</strong>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
          <button type="button" onClick={() => runCommand("TEST_WDT")} disabled={Boolean(busy)} style={button}>F0 · WDT</button>
          <button type="button" onClick={() => runCommand("TEST_STACK")} disabled={Boolean(busy)} style={button}>F1 · STACK</button>
          <button type="button" onClick={() => runCommand("TEST_TIMER0")} disabled={Boolean(busy)} style={button}>F2 · TIMER0</button>
        </div>
      </section>

      <section style={card}>
        <strong style={{ color: "#00dfff" }}>Actividad del protocolo</strong>
        <p style={{ color: message.startsWith("Enviando") ? "#facc15" : "#b8c2cc", lineHeight: 1.5 }}>{message}</p>
        <p style={{ color: "#8fa2b4", fontSize: 12 }}>Destino {selectedSubsystem?.name} · SUB {hexByte(selectedSubsystem?.address || 0)}. Las direcciones I²C 0x10/0x20/etc. son internas del maestro y no aparecen aquí.</p>
        {lastFrame?.frameHex && <FrameInspector hex={lastFrame.frameHex} direction="RX" label={`${lastFrame.commandName} · SEQ ${lastFrame.seq}`} defaultOpen />}
      </section>

      <p style={{ color: "#8fa2b4", fontSize: 12, margin: 0 }}>Los bytes se envían exactamente como 7E | SUB | CMD | SEQ | LEN | PAYLOAD | CRC8, sin CR ni LF.</p>
    </div>
  );
}
